import { computed } from 'vue';
import type { TableCell } from '../model/form.types';
import type { PermissionsOutline } from '../model/form-permissions-selection.store';
import {
  RULE_HIGHLIGHT_WIDTH,
  SIDES,
  isBorderSideActive,
  outerBorderHalf,
  ruleHighlightInset,
  type BorderSideName,
} from '../lib/cell-border-geometry';
import { sanitizeColor } from '../lib/color-sanitizer';
import type { FormBuilderInteractionMode } from '../lib/interaction-mode';
import { TABLE_BORDER_DEFAULTS, TABLE_CONSTRAINTS } from '../lib/table-constants';

export type CellBorderPattern =
  | 'all'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top-bottom'
  | 'left-right'
  | 'none';

export interface TableCellStyleContext {
  cell: TableCell;
  isSelected?: boolean;
  interactionMode?: FormBuilderInteractionMode;
  showRulesMenu?: boolean;
  isDraftRuleTarget?: boolean;
  hasAppliedRule?: boolean;
  permissionOutline?: PermissionsOutline | null;
  showSelectionFill?: boolean;
  gapRowsBefore?: number;
  isChunkTop?: boolean;
  isChunkBottom?: boolean;
  totalRows: number;
  totalColumns: number;
  borderWidth?: number;
  borderColor?: string;
  borderStyle?: 'solid' | 'dashed' | 'dotted';
  borderPattern?: CellBorderPattern;
}

type CellStyleMap = Record<string, string | undefined>;

const BORDER_COLOR = 'var(--color-gray-02)';
const BORDER_CSS_KEY: Record<BorderSideName, string> = {
  top: 'borderTop',
  right: 'borderRight',
  bottom: 'borderBottom',
  left: 'borderLeft',
};
const PERMISSION_SELECTED_OUTLINE = 'inset 0 0 0 2px var(--color-rule-border)';
const PERMISSION_APPLIED_OUTLINE = 'inset 0 0 0 2px var(--theme-secondary-ink)';
const SELECTED_CELL_OVERLAY =
  'inset 0 0 0 9999px rgba(208, 208, 208, 0.3), inset 0 0 0 1px rgba(130, 130, 130, 0.8)';

function isOuterBorderVisible(side: BorderSideName, pattern: CellBorderPattern | undefined): boolean {
  switch (pattern) {
    case 'none':
      return false;
    case 'all':
    case undefined:
      return true;
    case 'top':
    case 'right':
    case 'bottom':
    case 'left':
      return side === pattern;
    case 'top-bottom':
      return side === 'top' || side === 'bottom';
    case 'left-right':
      return side === 'left' || side === 'right';
    default:
      return true;
  }
}

function resolveCellBackgroundColor(backgroundColor: string | undefined): string | undefined {
  if (backgroundColor) return sanitizeColor(backgroundColor, 'transparent');
  return undefined;
}

function resolveCellStyleBackground(
  interactionMode: TableCellStyleContext['interactionMode'],
  isSelected: boolean | undefined,
  rulesBackground: string | undefined,
  backgroundColor: string | undefined
): string | undefined {
  if (interactionMode === 'permissions' && rulesBackground) return rulesBackground;
  const configured = resolveCellBackgroundColor(backgroundColor);
  if (configured) return configured;
  if (rulesBackground) return rulesBackground;
  if (interactionMode !== 'rules' && isSelected) return 'var(--color-white-01)';
  return undefined;
}

function resolveCellBoxShadow(
  hasAppliedRule: boolean,
  isSelected: boolean | undefined
): string | undefined {
  if (hasAppliedRule || !isSelected) return undefined;
  return SELECTED_CELL_OVERLAY;
}

function outerBorderSide(
  isEdge: boolean,
  side: BorderSideName,
  borderPattern: CellBorderPattern | undefined,
  outerBorder: string,
  interiorBorder?: string
): string | undefined {
  if (!isEdge) return interiorBorder;
  return isOuterBorderVisible(side, borderPattern) ? outerBorder : undefined;
}

function buildOuterBorders(
  isFirstRow: boolean,
  isFirstCol: boolean,
  isLastRow: boolean,
  isLastCol: boolean,
  outerBorder: string,
  cssWidth: Record<BorderSideName, number>,
  borderPattern: CellBorderPattern | undefined
): Pick<CellStyleMap, 'borderTop' | 'borderLeft' | 'borderRight' | 'borderBottom'> {
  return {
    borderTop: outerBorderSide(isFirstRow, 'top', borderPattern, outerBorder),
    borderLeft: outerBorderSide(isFirstCol, 'left', borderPattern, outerBorder),
    borderRight: outerBorderSide(
      isLastCol,
      'right',
      borderPattern,
      outerBorder,
      `${cssWidth.right}px solid ${BORDER_COLOR}`
    ),
    borderBottom: outerBorderSide(
      isLastRow,
      'bottom',
      borderPattern,
      outerBorder,
      `${cssWidth.bottom}px solid ${BORDER_COLOR}`
    ),
  };
}

/** Builds the inline cell styles + optional border-overlay styles for a table cell. */
export function useTableCellStyles(props: TableCellStyleContext) {
  const showAppliedRuleHighlight = computed(
    () => props.interactionMode === 'rules' && !!props.hasAppliedRule
  );

  function resolveRulesBackground(): string | undefined {
    if (props.interactionMode !== 'rules') return undefined;
    if (showAppliedRuleHighlight.value) return '#ffffff';
    if (props.isSelected || props.isDraftRuleTarget) return 'var(--color-gray-08)';
    return undefined;
  }

  const isPermissionsMode = computed(() => props.interactionMode === 'permissions');

  function resolvePermissionsBackground(): string | undefined {
    if (!isPermissionsMode.value) return undefined;
    if (props.permissionOutline === 'picked') return 'var(--color-gray-08)';
    return props.permissionOutline === 'selected' && props.showSelectionFill
      ? 'var(--color-gray-08)'
      : undefined;
  }

  function resolvePermissionsBoxShadow(): string | undefined {
    if (props.permissionOutline === 'selected') return PERMISSION_SELECTED_OUTLINE;
    return props.permissionOutline === 'applied' || props.permissionOutline === 'picked'
      ? PERMISSION_APPLIED_OUTLINE
      : undefined;
  }

  const cellBorderContext = computed(() => {
    const { rowIndex, colIndex, rowSpan = 1, colSpan = 1 } = props.cell;
    const outerWidth = props.borderWidth ?? TABLE_BORDER_DEFAULTS.WIDTH;
    const isFirstRow = rowIndex === 0;
    const isFirstCol = colIndex === 0;
    const isLastRow = rowIndex + rowSpan >= props.totalRows;
    const isLastCol = colIndex + colSpan >= props.totalColumns;
    return {
      outerWidth,
      isFirstRow,
      isFirstCol,
      isLastRow,
      isLastCol,
      cssWidth: {
        top: isFirstRow ? outerWidth : 0,
        right: isLastCol ? outerWidth : 1,
        bottom: isLastRow ? outerWidth : 1,
        left: isFirstCol ? outerWidth : 0,
      } as Record<BorderSideName, number>,
    };
  });

  const borderOverlayStyles = computed((): CellStyleMap | null => {
    const border = props.cell.properties?.border;
    if (!border) return null;

    const isActive = (s: BorderSideName) => isBorderSideActive(border[s]);
    if (!SIDES.some(isActive)) return null;

    const { cssWidth } = cellBorderContext.value;

    const offset = (s: BorderSideName): string => {
      if (isActive(s)) {
        return `-${outerBorderHalf(s, border[s]!.width ?? 0)}px`;
      }
      return cssWidth[s] > 0 ? `-${cssWidth[s]}px` : '0px';
    };

    const sideStyle = (s: BorderSideName): string => {
      const d = border[s];
      if (!isActive(s)) return 'none';
      return `${d!.width}px ${d!.style} ${sanitizeColor(d!.color, '#e3e3e3')}`;
    };

    return Object.fromEntries(
      SIDES.flatMap((s) => [
        [BORDER_CSS_KEY[s], sideStyle(s)],
        [s, offset(s)],
      ])
    );
  });

  const ruleHighlightStyles = computed((): CellStyleMap | null => {
    if (!showAppliedRuleHighlight.value) return null;

    const border = props.cell.properties?.border;

    return {
      ...Object.fromEntries(SIDES.map((s) => [s, `${ruleHighlightInset(s, border?.[s])}px`])),
      border: `${RULE_HIGHLIGHT_WIDTH}px solid var(--color-rule-border)`,
    };
  });

  const outerBorder = computed(() => {
    const outerColor = sanitizeColor(props.borderColor, TABLE_BORDER_DEFAULTS.COLOR);
    const style = props.borderStyle ?? TABLE_BORDER_DEFAULTS.STYLE;
    return `${cellBorderContext.value.outerWidth}px ${style} ${outerColor}`;
  });

  const borderWidths = computed(() => cellBorderContext.value.cssWidth);

  const cellStyles = computed(() => {
    const { colSpan = 1, rowSpan = 1, properties } = props.cell;
    const { isFirstRow, isFirstCol, isLastRow, isLastCol, cssWidth } = cellBorderContext.value;
    const outerBorderValue = outerBorder.value;
    const borderPattern = props.borderPattern ?? TABLE_BORDER_DEFAULTS.PATTERN;

    const { rowIndex, colIndex } = props.cell;
    const rowStartLine = rowIndex + (props.gapRowsBefore ?? 0) + 1;
    const colStartLine = colIndex + 1;

    const styles: CellStyleMap = {
      gridColumn: `${colStartLine} / span ${colSpan}`,
      gridRow: `${rowStartLine} / span ${rowSpan}`,
      backgroundColor: resolveCellStyleBackground(
        props.interactionMode,
        props.isSelected,
        resolveRulesBackground() ?? resolvePermissionsBackground(),
        properties?.backgroundColor
      ),
      boxShadow: isPermissionsMode.value
        ? resolvePermissionsBoxShadow()
        : resolveCellBoxShadow(showAppliedRuleHighlight.value, props.isSelected),
      padding: `${properties?.padding ?? TABLE_CONSTRAINTS.DEFAULT_CELL_PADDING}px`,
      ...buildOuterBorders(
        isFirstRow,
        isFirstCol,
        isLastRow,
        isLastCol,
        outerBorderValue,
        cssWidth,
        borderPattern
      ),
    };

    // Close the top/bottom edge of a page-break chunk so each chunk looks like a
    // complete, separate table rather than a bisected one.
    if (props.isChunkBottom) {
      styles.borderBottom = outerBorderValue;
    }
    if (props.isChunkTop) {
      styles.borderTop = outerBorderValue;
    }

    const border = properties?.border;
    if (border) {
      for (const s of SIDES) {
        if (isBorderSideActive(border[s])) styles[BORDER_CSS_KEY[s]] = 'none';
      }
    }

    if (borderOverlayStyles.value) {
      const maxWidth = Math.max(...SIDES.map((s) => border?.[s]?.width ?? 0));
      styles.zIndex = String(1 + maxWidth);
    }

    // `auto` (not a value) on purpose: it overrides the `z-[1]` selected class without making the
    // cell a stacking context, so the rules menu's own `z-40` applies at grid level and the report
    // badge overlay is not covered by this cell.
    if (props.showRulesMenu) {
      styles.zIndex = 'auto';
    }

    return styles;
  });

  return {
    cellStyles,
    borderOverlayStyles,
    ruleHighlightStyles,
    showAppliedRuleHighlight,
    outerBorder,
    borderWidths,
  };
}
